/**
 * Honest checks for what an agent refers to (Builder sweep): a model, a knowledge collection or a
 * saved prompt that this platform doesn't have, and settings in the agent's file that nothing
 * uses when it runs. The data checks (ADR-0071) and the access checks (ADR-0087) cover steps,
 * values, systems, data levels and roles; these cover the rest, so "No problems found" means the
 * agent can really work.
 *
 * Lists the console could not load are "unknown" and never reported.
 */
import type { PlainIssue } from "./plain";
import { memberName, memberStep, runningKind, teamMismatch, type Topology } from "./team";
import type { AgentSpec, SpecNode } from "./types";

export interface ReferenceLists {
  /** Knowledge collection names of the organisation (retrieval service). */
  collections?: readonly string[];
  /** Model aliases the gateway offers. */
  models?: readonly string[];
  /** Saved prompt names (prompt library). */
  prompts?: readonly string[];
}

/** Settings kept in the agent's file that have no effect when it runs (a one-click removal). */
export type UnusedSetting = "team" | "card" | "escalate" | "fallback";

/** `prompts:/name@stage` or `prompts:/name/3` → name. */
export function promptName(ref: string | null | undefined): string | null {
  const m = /^prompts:\/([A-Za-z0-9][A-Za-z0-9_.-]*)(?:@[a-z][a-z0-9_-]*|\/\d+)?$/.exec(ref ?? "");
  return m ? (m[1] as string) : null;
}

const label = (n: SpecNode | undefined, lang: "ar" | "en", fallback: string): string => {
  const l = n?.label ? (lang === "ar" ? n.label.ar || n.label.en : n.label.en || n.label.ar) : "";
  return l || fallback;
};

export function referenceIssues(spec: AgentSpec, lists: ReferenceLists, lang: "ar" | "en" = "en"): PlainIssue[] {
  const out: PlainIssue[] = [];
  const s = spec.spec;
  const nodes = s.graph.nodes;
  const step = (n: SpecNode) => label(n, lang, n.id);

  // Models the gateway doesn't offer: the agent's, each AI step's own and each AI decision's.
  if (lists.models) {
    const known = new Set(lists.models);
    if (s.model.alias && !known.has(s.model.alias)) out.push({ code: "modelMissing", params: { model: s.model.alias }, field: "model", raw: `model alias ${s.model.alias} is not offered by the gateway` });
    for (const n of nodes) {
      const alias = n.type === "llm" ? (n.model as { alias?: string } | undefined)?.alias : n.type === "decision" ? (typeof n.model === "string" ? n.model : undefined) : undefined;
      if (alias && alias !== s.model.alias && !known.has(alias)) out.push({ code: "modelMissingStep", params: { step: step(n), model: alias }, node: n.id, raw: `step ${n.id} uses model alias ${alias}, which the gateway does not offer` });
    }
  }
  // Knowledge collections that don't exist in this organisation.
  if (lists.collections) {
    const known = new Set(lists.collections);
    for (const k of s.knowledge ?? []) {
      if (known.has(k.collection)) continue;
      const user = nodes.find((n) => n.type === "retrieval" && n.collection === k.collection);
      out.push({ code: "collectionMissing", params: { collection: k.collection }, ...(user ? { node: user.id } : { field: "knowledge" }), raw: `knowledge collection ${k.collection} does not exist in this organisation` });
    }
  }
  // Saved prompts that aren't in the prompt library.
  if (lists.prompts) {
    const known = new Set(lists.prompts);
    for (const n of nodes) {
      if (n.type !== "llm") continue;
      const ref = (n.prompt as { ref?: string | null } | undefined)?.ref;
      if (typeof ref !== "string" || !ref) continue;
      const name = promptName(ref);
      if (name && !known.has(name)) out.push({ code: "promptMissing", params: { step: step(n), prompt: name }, node: n.id, raw: `step ${n.id} uses saved prompt ${ref}, which is not in the prompt library` });
    }
  }
  // Settings nothing reads at run time: said, with a one-click removal.
  // Teams of agents (ADR-0093): a supervisor or handoff team runs; an old debate team does not.
  const team = s.topology as Topology | null | undefined;
  if (team && !runningKind(team)) out.push({ code: "unusedTeam", params: {}, field: "team", fix: { kind: "clear", what: "team" }, raw: "a debate team no longer runs" });
  if (team && runningKind(team)) {
    const stale = teamMismatch(spec);
    if (stale.length) out.push({ code: "teamStale", params: { members: stale.map(memberName).join(", ") }, field: "team", fix: { kind: "rebuildTeam" }, raw: `the steps do not run team members ${stale.join(", ")}` });
    for (const m of team.members) {
      if (!m.description || !(m.description.en || m.description.ar).trim()) {
        const at = nodes.find((n) => n.id === memberStep(m.agent));
        out.push({ code: "teamNoDescription", params: { member: label(at, lang, memberName(m.agent)) }, ...(at ? { node: at.id } : { field: "team" }), raw: `team member ${m.agent} has no description` });
      }
    }
  }
  // The agent card (A2A): published with nothing but the description, other agents can't tell what it does.
  if (s.a2a?.publish_card && !(s.a2a.skills ?? []).length) out.push({ code: "cardNoSkills", params: {}, field: "card", raw: "the agent card lists no skills" });
  // approvals[].escalate_to is used at run time (ADR-0097): reminders and escalation.
  const fallback = s.model.fallback_alias || nodes.some((n) => n.type === "llm" && (n.model as { fallback_alias?: string | null } | undefined)?.fallback_alias);
  if (fallback) out.push({ code: "unusedFallback", params: {}, field: "model", fix: { kind: "clear", what: "fallback" }, raw: "model.fallback_alias is not used at run time (the gateway chooses backups)" });
  return out;
}

/** Removes a setting nothing uses at run time (pure). */
export function clearSetting(spec: AgentSpec, what: UnusedSetting): AgentSpec {
  const s = spec.spec;
  if (what === "team") {
    const { topology: _t, ...rest } = s;
    return { ...spec, spec: rest as AgentSpec["spec"] };
  }
  if (what === "card") return { ...spec, spec: { ...s, a2a: { ...(s.a2a ?? {}), publish_card: false } } };
  if (what === "escalate") return { ...spec, spec: { ...s, approvals: (s.approvals ?? []).map((r) => ({ ...r, escalate_to: [] })) } };
  const { fallback_alias: _f, ...model } = s.model;
  const nodes = s.graph.nodes.map((n) => {
    if (n.type !== "llm" || !n.model) return n;
    const { fallback_alias: _x, ...m } = n.model as Record<string, unknown>;
    return { ...n, model: m };
  });
  return { ...spec, spec: { ...s, model, graph: { ...s.graph, nodes } } };
}
